import { useEffect, useState, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { MotionConfig } from "framer-motion";
import Lenis from "lenis";
import { Toaster } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CookieConsent from "@/components/CookieConsent";
import { captureUtm } from "@/lib/utm";
import { lazyLoadScript } from "@/lib/lazyLoad";
import Home from "@/pages/Home";

const VyayanHub = lazy(() => import("@/pages/VyayanHub"));
const SocietyManagement = lazy(() => import("@/pages/SocietyManagement"));
const RestaurantManagement = lazy(() => import("@/pages/RestaurantManagement"));
const CustomSoftware = lazy(() => import("@/pages/CustomSoftware"));
const CloudSolutions = lazy(() => import("@/pages/CloudSolutions"));
const SalesforceDevelopment = lazy(() => import("@/pages/SalesforceDevelopment"));
const DataAndAI = lazy(() => import("@/pages/DataAndAI"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const Insights = lazy(() => import("@/pages/Insights"));
const Legal = lazy(() => import("@/pages/Legal"));
const ThankYou = lazy(() => import("@/pages/ThankYou"));
const Chatbot = lazy(() => import("@/components/Chatbot"));

function PageLoader() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center" data-testid="page-loader">
      <span className="flex h-10 w-10 animate-pulse items-center justify-center rounded-sm bg-brand font-heading text-xl font-bold text-white">A</span>
    </div>
  );
}

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => {
        document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 120);
      return () => clearTimeout(t);
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function useSmoothScroll() {
  useEffect(() => {
    const lenis = new Lenis({ autoRaf: true, lerp: 0.09 });
    return () => lenis.destroy();
  }, []);
}

function useAnalytics() {
  useEffect(() => {
    const id = process.env.REACT_APP_GA4_ID;
    if (!id) return;

    // Check if user has given consent for cookies
    const consentStr = localStorage.getItem("adika_cookie_consent");
    if (!consentStr) return; // No consent yet, don't load GA4

    const consent = JSON.parse(consentStr);
    if (!consent.accepted) return; // User rejected cookies

    // Load GA4 after page is interactive (using browser idle time)
    // This improves First Contentful Paint by not blocking render
    const loadGA4 = () => {
      const s = document.createElement("script");
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
      document.head.appendChild(s);
      window.dataLayer = window.dataLayer || [];
      window.gtag = function () {
        window.dataLayer.push(arguments);
      };
      window.gtag("js", new Date());
      window.gtag("config", id);
    };

    // Use requestIdleCallback for zero blocking, with timeout fallback
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(() => loadGA4(), { timeout: 4000 });
    } else {
      // Fallback for browsers without requestIdleCallback
      setTimeout(loadGA4, 2000);
    }
  }, []);
}

function useDeferredChatbot() {
  const [load, setLoad] = useState(false);
  useEffect(() => {
    captureUtm();
    if ("requestIdleCallback" in window) {
      const id = window.requestIdleCallback(() => setLoad(true), { timeout: 4000 });
      return () => window.cancelIdleCallback(id);
    }
    const t = setTimeout(() => setLoad(true), 2500);
    return () => clearTimeout(t);
  }, []);
  return load;
}

function NotFound() {
  return (
    <main className="mx-auto max-w-7xl px-5 py-32 lg:px-8" data-testid="not-found-page">
      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-brand">404</p>
      <h1 className="mt-4 font-heading text-5xl tracking-tight text-gray-950">This page moved, or never existed.</h1>
      <a href="/" data-testid="not-found-home-link" className="mt-8 inline-block rounded-sm bg-brand px-7 py-3.5 text-sm font-semibold text-white">
        Back to home
      </a>
    </main>
  );
}

function App() {
  useSmoothScroll();
  useAnalytics();
  const loadChatbot = useDeferredChatbot();
  return (
    <HelmetProvider>
      <MotionConfig reducedMotion="user">
        <div className="grain min-h-screen bg-white text-gray-950">
          <BrowserRouter>
            <ScrollManager />
            <Header />
            <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/products/vyayanhub" element={<VyayanHub />} />
              <Route path="/products/society-management" element={<SocietyManagement />} />
              <Route path="/products/restaurant-management" element={<RestaurantManagement />} />
              <Route path="/services/custom-software-development" element={<CustomSoftware />} />
              <Route path="/services/cloud-solutions" element={<CloudSolutions />} />
              <Route path="/services/salesforce-development" element={<SalesforceDevelopment />} />
              <Route path="/services/data-and-ai" element={<DataAndAI />} />
              <Route path="/about" element={<About />} />
              <Route path="/insights" element={<Insights />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy-policy" element={<Legal doc="privacy" />} />
              <Route path="/terms-of-service" element={<Legal doc="terms" />} />
              <Route path="/cookie-policy" element={<Legal doc="cookies" />} />
              <Route path="/thank-you/:type" element={<ThankYou />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
            <Footer />
            <CookieConsent />
            {loadChatbot && (
              <Suspense fallback={null}>
                <Chatbot />
              </Suspense>
            )}
          </BrowserRouter>
          <Toaster position="bottom-center" richColors />
        </div>
      </MotionConfig>
    </HelmetProvider>
  );
}

export default App;
